import React from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { CheckCircle } from '@mui/icons-material'
import LoadingButton from '@app/src/components/LoadingButton'
import type { ExtraRecordColumn } from './EditableRecordsTable'
import EditableRecordsTable from './EditableRecordsTable'
import { useTabularImportEntity } from '../TabularImportEntityContext'

export type ReviewStepProps = {
  sessionId: string
  totalRows: number
  successfulRows: number
  validRows: number
  incompleteRows: number
  failedRows: number
  isImporting: boolean
  isUpdatingRecord: boolean
  formatMessage: (descriptor: { id: string }) => string
  onGoBackToMappings: () => void
  onFinalImport: () => void
  onClose: () => void
  extraColumns?: ExtraRecordColumn[]
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  sessionId,
  totalRows,
  successfulRows,
  validRows,
  incompleteRows,
  failedRows,
  isImporting,
  isUpdatingRecord,
  formatMessage,
  onGoBackToMappings,
  onFinalImport,
  onClose,
  extraColumns,
}) => {
  const { requiredFields } = useTabularImportEntity()

  return (
    <Stack spacing={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" gutterBottom>
            Validate and import
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {totalRows} rows processed. Review the results below and select which records to import.
          </Typography>
        </Box>
      </Box>

      {/* Summary */}
      <Box display="flex" gap={2} flexWrap="wrap">
        <Alert severity="success" sx={{ flex: 1 }}>
          <Typography variant="subtitle2">Ready to import: {validRows} (with all required fields)</Typography>
        </Alert>
        {incompleteRows > 0 && (
          <Alert severity="warning" sx={{ flex: 1 }}>
            <Typography variant="subtitle2">
              Incomplete: {incompleteRows} (missing {requiredFields.join(' or ')})
            </Typography>
          </Alert>
        )}
        {failedRows > 0 && (
          <Alert severity="error" sx={{ flex: 1 }}>
            <Typography variant="subtitle2">Failed: {failedRows}</Typography>
          </Alert>
        )}
      </Box>

      {/* Editable Records — owns its own data fetching */}
      <Box>
        <EditableRecordsTable
          sessionId={sessionId}
          totalRows={totalRows}
          successfulRows={successfulRows}
          failedRows={failedRows}
          extraColumns={extraColumns}
        />
      </Box>

      {/* Actions */}
      <Box display="flex" justifyContent="space-between" gap={2}>
        <Button onClick={onGoBackToMappings}>Go back</Button>
        <Box display="flex" gap={2}>
          {validRows > 0 && (
            <LoadingButton
              loading={isImporting}
              variant="contained"
              onClick={onFinalImport}
              startIcon={<CheckCircle />}
            >
              Continue with {validRows} record{validRows !== 1 ? 's' : ''}
            </LoadingButton>
          )}
          {validRows === 0 && (
            <Button variant="outlined" onClick={onClose}>
              {formatMessage({ id: 'general.close' })}
            </Button>
          )}
        </Box>
      </Box>

      {validRows > 0 && (
        <Alert severity="info">
          {validRows} record{validRows !== 1 ? 's' : ''} will proceed to deduplication review.
          {incompleteRows > 0 &&
            ` ${incompleteRows} incomplete record${incompleteRows !== 1 ? 's' : ''} will be skipped (missing required fields).`}
        </Alert>
      )}
      {validRows === 0 && incompleteRows > 0 && (
        <Alert severity="error">
          No records can be imported. All {incompleteRows} record
          {incompleteRows !== 1 ? 's are' : ' is'} missing required fields ({requiredFields.join(' and/or ')}). Please
          edit the records to provide the missing information.
        </Alert>
      )}
    </Stack>
  )
}

export default ReviewStep
